import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { FaqAdminCategory, FaqCategory, FaqQuestion, FaqSearchMiss } from '@shared/schemas';

import { moveItem } from '../../_shared/domain/move-item';
import {
  type AdminFaqState,
  type DeletedItem,
  initialState,
  type SearchMissNotice,
} from './admin-faq.state';

/** Positions mirror the array order, dense 0..n-1, as the backend keeps them. */
function renumber<T extends { position: number }>(items: T[]): void {
  items.forEach((item, position) => {
    item.position = position;
  });
}

function findQuestion(state: AdminFaqState, id: string) {
  for (const category of state.categories) {
    const index = category.questions.findIndex((q) => q.id === id);
    if (index !== -1) return { category, index };
  }
  return null;
}

/**
 * Admin FAQ slice (#735). Reducers are pure state transitions only; every fetch
 * and write lives in the saga, which dispatches these as it progresses. The page
 * itself only dispatches `outcomesAcknowledged`, `outcomesCleared` and
 * `lastDeletedCleared`.
 */
const slice = createSlice({
  name: 'adminFaq',
  initialState,
  reducers: {
    // ── Tree load ──
    loadStarted: (state) => {
      state.loadStatus = 'loading';
    },
    loadSucceeded: (state, action: PayloadAction<FaqAdminCategory[]>) => {
      state.categories = action.payload;
      state.loadStatus = 'loaded';
    },
    loadFailed: (state) => {
      state.loadStatus = 'failed';
    },

    // ── Any write ──
    mutationStarted: (state, action: PayloadAction<string>) => {
      state.pendingRequestId = action.payload;
    },
    mutationSucceeded: (state, action: PayloadAction<string>) => {
      state.pendingRequestId = null;
      state.outcomes.push({ requestId: action.payload, status: 'succeeded', error: null });
    },
    mutationFailed: (state, action: PayloadAction<{ requestId: string; error: string }>) => {
      state.pendingRequestId = null;
      state.outcomes.push({ ...action.payload, status: 'failed' });
    },
    /** The page has reacted to these outcomes (closed a dialog, shown the error). */
    outcomesAcknowledged: (state, action: PayloadAction<string[]>) => {
      const seen = new Set(action.payload);
      state.outcomes = state.outcomes.filter((o) => !seen.has(o.requestId));
    },
    /**
     * Forget every outcome, on the page's mount: a write that ended after the
     * admin left (a failed Undo) must not toast on their return.
     */
    outcomesCleared: (state) => {
      state.outcomes = [];
    },

    // ── Categories ──
    categoryCreated: (state, action: PayloadAction<FaqCategory>) => {
      state.categories.push({ ...action.payload, questions: [] });
    },
    categoryUpdated: (state, action: PayloadAction<FaqCategory>) => {
      const category = state.categories.find((c) => c.id === action.payload.id);
      if (category) Object.assign(category, action.payload);
    },
    categoryDeleted: (state, action: PayloadAction<DeletedItem>) => {
      state.categories = state.categories.filter((c) => c.id !== action.payload.id);
      renumber(state.categories);
      state.lastDeleted = action.payload;
    },
    /** Optimistic: applied before the server answers; the saga reloads on refusal. */
    categoryMoved: (state, action: PayloadAction<{ id: string; toIndex: number }>) => {
      const from = state.categories.findIndex((c) => c.id === action.payload.id);
      if (from === -1) return;
      state.categories = moveItem(state.categories, from, action.payload.toIndex);
      renumber(state.categories);
    },

    // ── Questions ──
    questionCreated: (state, action: PayloadAction<FaqQuestion>) => {
      const category = state.categories.find((c) => c.id === action.payload.category_id);
      category?.questions.push(action.payload);
    },
    questionUpdated: (state, action: PayloadAction<FaqQuestion>) => {
      const found = findQuestion(state, action.payload.id);
      if (found) found.category.questions[found.index] = action.payload;
    },
    questionDeleted: (state, action: PayloadAction<DeletedItem>) => {
      const found = findQuestion(state, action.payload.id);
      if (found) {
        found.category.questions.splice(found.index, 1);
        renumber(found.category.questions);
      }
      state.lastDeleted = action.payload;
    },
    /**
     * Optimistic: out of its category, into `categoryId` at `index` (clamped),
     * like the backend's move. The saga reloads on refusal.
     */
    questionMoved: (
      state,
      action: PayloadAction<{ id: string; categoryId: string; index: number }>,
    ) => {
      const { id, categoryId, index } = action.payload;
      const found = findQuestion(state, id);
      const target = state.categories.find((c) => c.id === categoryId);
      if (!found || !target) return;
      const [question] = found.category.questions.splice(found.index, 1);
      question.category_id = categoryId;
      const at = Math.max(0, Math.min(Math.trunc(index), target.questions.length));
      target.questions.splice(at, 0, question);
      renumber(found.category.questions);
      renumber(target.questions);
    },

    // ── Searches with no answer ──
    searchMissesLoaded: (state, action: PayloadAction<FaqSearchMiss[]>) => {
      state.searchMisses = { items: action.payload, status: 'loaded' };
    },
    searchMissesFailed: (state) => {
      state.searchMisses.status = 'failed';
    },
    /** Dismissed on screen, before the server answers. */
    searchMissHidden: (state, action: PayloadAction<string>) => {
      state.searchMisses.items = state.searchMisses.items.filter(
        (m) => m.normalized_query !== action.payload,
      );
    },
    /** Undo of a dismiss: back where it was. */
    searchMissShownAgain: (
      state,
      action: PayloadAction<{ item: FaqSearchMiss; index: number }>,
    ) => {
      const { item, index } = action.payload;
      if (state.searchMisses.items.some((m) => m.normalized_query === item.normalized_query))
        return;
      state.searchMisses.items.splice(Math.min(index, state.searchMisses.items.length), 0, item);
    },

    searchMissNoticed: (state, action: PayloadAction<SearchMissNotice>) => {
      state.searchMissNotice = action.payload;
    },
    searchMissNoticeCleared: (state) => {
      state.searchMissNotice = null;
    },

    // ── Undo ──
    /** Tree reloaded after an Undo: the item is back where it was. */
    restored: (state, action: PayloadAction<FaqAdminCategory[]>) => {
      state.categories = action.payload;
      state.lastDeleted = null;
    },
    /** The Undo snackbar went away; nothing is offered for restore any more. */
    lastDeletedCleared: (state) => {
      state.lastDeleted = null;
    },
  },
});

export const adminFaqActions = slice.actions;
export const {
  searchMissHidden,
  searchMissShownAgain,
  searchMissNoticeCleared,
  outcomesAcknowledged,
  outcomesCleared,
  lastDeletedCleared,
} = slice.actions;
export const adminFaqReducer = slice.reducer;
